// src/data/productsData.js
export const products = [
  // Planning Intelligence
  {
    id: "demand-forecasting",
    category: "Planning Intelligence",
    name: "Demand Forecasting",
    tagline: "Predict demand with precision",
    icon: "BarChart3",
    badge: "New",
    description:
      "KGS Technology Demand Forecasting combines historical demand data, external market indicators, and real-time signals to generate highly accurate forecasts that drive smarter inventory, production, and fulfillment decisions.",
    benefits: [
      { title: "AI-Powered", icon: "Brain" },
      { title: "Real-Time", icon: "Clock" },
      { title: "Enterprise-Scale", icon: "Building2" },
      { title: "Fast Deployment", icon: "Zap" },
    ],
    ctaText: "Request a Demo",
    metrics: [
      { value: "25%", label: "Forecast Accuracy Improvement" },
      { value: "3x", label: "Faster Demand Planning Cycles" },
      { value: "40%", label: "Reduction in Safety Stock" },
      { value: "18%", label: "Increase in Service Level" },
    ],
    discover: {
      title: "Discover Demand Forecasting",
      subtitle: "AI-powered demand clarity where precision meets speed",
      description:
        "Traditional forecasting methods struggle to keep pace with volatile demand patterns. KGS Technology Demand Forecasting uses AI to continuously learn from patterns in historical demand data, detect anomalies, and adapt to shifts in real-time market signals.",
      features: [
        "SKU, plant, region, and channel-level forecasting",
        "Seasonality, trend, and promotional uplift modeling",
        "Demand anomaly detection and alerting",
      ],
    },
    capabilities: [
      {
        title: "SKU, plant, region, and channel-level demand planning",
        icon: "Package",
        color: "red",
      },
      {
        title: "Seasonality, trend, and promotional uplift modeling",
        icon: "TrendingUp",
        color: "green",
      },
      {
        title: "Demand anomaly detection and alerting",
        icon: "Target",
        color: "blue",
      },
      {
        title: "Scenario simulation for pricing, media, and networks",
        icon: "Sparkles",
        color: "purple",
      },
      {
        title: "Multi-echelon inventory optimization",
        icon: "Layers",
        color: "teal",
      },
      {
        title: "Promotional signal integration and lift prediction",
        icon: "Megaphone",
        color: "orange",
      },
      {
        title: "Pre-built demand planning reports and dashboards",
        icon: "BarChart",
        color: "purple",
      },
      {
        title: "Enterprise-wide controllability and shared planning layer",
        icon: "Network",
        color: "cyan",
      },
    ],
    outcomes: [
      {
        title: "Improved forecast accuracy by 20-35% against rule-based models",
        icon: "TrendingUp",
      },
      {
        title:
          "Reduced excess inventory as an emergency execution buffer (EBO)",
        icon: "Package",
      },
      {
        title: "Stronger revenue productivity and on-budget performance",
        icon: "DollarSign",
      },
      { title: "Improved cross-functional planning alignment", icon: "Users" },
      {
        title: "Lower safety stock requirements through precision forecasting",
        icon: "ShieldCheck",
      },
      {
        title: "Minimum waste and execution costs from overstocking",
        icon: "Trash2",
      },
    ],
    useCases: [
      {
        title: "Seasonal Demand Planning",
        icon: "Calendar",
        description:
          "Automatically plan against fluctuations for seasonal product cycles, promotional events, and holiday spikes while maintaining inventory accuracy.",
      },
      {
        title: "New Product Launch",
        icon: "Rocket",
        description:
          "Leverage analog modeling and market signals to predict demand for products with limited sales history, reducing stock-out risk.",
      },
      {
        title: "High-Channel Optimization",
        icon: "ShoppingCart",
        description:
          "Synchronize forecasts across retail, e-commerce, wholesale, and direct channels ensuring balanced allocation and fulfillment.",
      },
    ],
    aiDifferentiators: [
      {
        title: "Multi-Source Demand Sensing",
        description:
          "KGS Technology ingests real-time signals from POS transactions, web traffic, social sentiment, economic indicators, and supply chain events through AI-enabled demand sensing that refines short-term forecasts.",
      },
      {
        title: "Algorithmic Tier-Skin Matching",
        description:
          "Our AI automatically clusters SKUs by demand behavior characteristics, then selects the best-fit forecast model. Seasonal items use decomposition algorithms; stable goods use exponential smoothing; erratic SKUs use probabilistic models.",
      },
      {
        title: "Self-Correcting Accuracy",
        description:
          "As actuals arrive, the system compares them to high-parameter sets—re-assess forecasting model's suitability and retrains selected algorithms to improve future performance.",
      },
    ],
    beyondSpreadsheets: [
      {
        title: "Multi-Horizon Accuracy",
        description:
          "Generate reliable, monthly, and quarterly forecasts with confidence intervals. Track forecast errors dynamically—no need to wait for planning cycles to adjust.",
      },
      {
        title: "Promotional Uplift Modeling",
        description:
          "Isolate the demand lift from promotional campaigns—accounting for baseline demand, cannibalization, and halo effects across SKUs and timeframes.",
      },
      {
        title: "Anomaly Detection & Auto-Correction",
        description:
          "Identify outliers in historical sales data caused by stock-outs, one-time events, data errors—and auto-correct them in forecast inputs to prevent model distortion.",
      },
      {
        title: "Scenario Simulation Engine",
        description:
          "Run what-if scenarios on pricing changes, new product launches, supplier disruptions, and transport—with probabilistic demand ranges to guide risk-balanced planning.",
      },
    ],
    intelligenceHub: {
      title: "Demand Intelligence Hub",
      description:
        "Demand Forecasting gives planners, merchandisers, and supply chain leaders access to a single source of consensus demand across SKUs, DCs, locations, and channels—calibrated against real-time market data and operational constraints.",
      features: [
        "Forecast accuracy breakdowns ranked by SKU, region, and planner",
        "Bias-tuning dashboards showing systematic over- and under-forecasts",
        "Demand signal decomposition (baseline, trend, promo, seasonality)",
        "Integrated promotional calendars with uplift and overlay per campaign",
        "Planner collaboration interface with note sharing and approval workflows",
      ],
      metrics: [
        { value: "35%", label: "Forecast Accuracy Gain" },
        { value: "40%", label: "Less Safety Stock" },
        { value: "3x", label: "Faster Planning Cycles" },
        { value: "30M", label: "SKUs Forecasted Daily" },
      ],
    },
    relatedProducts: [
      {
        title: "Inventory Intelligence (MIST)",
        description:
          "Multi-echelon inventory optimization built on ML-enhanced demand signals. Plan safety stock, reorder points, and distribution strategies across your supply network with precision.",
      },
      {
        title: "Order Planning",
        description:
          "Convert demand forecasts into executable purchase and production orders with constraint-aware planning and automated order generation across suppliers and plants.",
      },
    ],
    cta: {
      title: "Ready to get started with Demand Forecasting?",
      subtitle:
        "See how KGS Technology Demand Forecasting can reduce your uncertainty and accelerate decision cycles.",
      buttons: [{ text: "Schedule a Demo", type: "primary", path: "/contact" }],
      additionalOptions: [
        "Take the product tour",
        "Explore ROI calculator",
        "Read case studies on demand forecasting",
      ],
    },
  },
  {
    id: "inventory-intelligence",
    category: "Planning Intelligence",
    name: "Inventory Intelligence (MIST)",
    tagline: "Multi-echelon inventory optimization at scale",
    icon: "Globe",
    description:
      "MIST (Multi-echelon Inventory Simulation Tool) optimizes safety stock, reorder points, and inventory placement across your entire network using AI-powered demand signals and constraint modeling.",
    metrics: [
      { value: "35%", label: "Reduction in Carrying Costs" },
      { value: "98%", label: "Service Level Achievement" },
      { value: "50%", label: "Decrease in Stockouts" },
      { value: "20%", label: "Inventory Turnover Increase" },
    ],
    discover: {
      title: "Discover Inventory Intelligence",
      subtitle: "Right inventory, right place, right time",
      description:
        "Traditional inventory planning treats each location independently, leading to overstocking or stockouts. MIST takes a network-wide view, optimizing inventory levels across all echelons from suppliers to distribution centers to retail locations.",
      features: [
        "Multi-echelon network optimization",
        "Dynamic safety stock calculation",
        "Demand variability and lead time modeling",
        "Service level target balancing",
      ],
    },
    capabilities: [
      {
        title: "Network-wide inventory optimization",
        icon: "Network",
        color: "blue",
      },
      {
        title: "Safety stock and reorder point calculation",
        icon: "Shield",
        color: "green",
      },
      {
        title: "Service level vs. cost trade-off analysis",
        icon: "Scale",
        color: "purple",
      },
      {
        title: "ABC/XYZ segmentation and policy setting",
        icon: "Grid",
        color: "orange",
      },
    ],
    useCases: [
      {
        title: "Multi-DC Network Optimization",
        icon: "Warehouse",
        description:
          "Optimize inventory positioning across regional and national distribution centers to minimize total network inventory while maintaining service levels.",
      },
      {
        title: "Seasonal Inventory Planning",
        icon: "CalendarRange",
        description:
          "Dynamically adjust safety stock levels and reorder policies based on seasonal demand patterns and promotional calendars.",
      },
      {
        title: "New Market Entry",
        icon: "MapPin",
        description:
          "Model optimal inventory levels for new locations or markets using demand forecasts and network flow constraints.",
      },
    ],
  },
  {
    id: "order-planning",
    category: "Planning Intelligence",
    name: "Order Planning",
    tagline: "Automated purchase and production planning",
    icon: "Truck",
    description:
      "Transform demand forecasts into executable orders with constraint-aware planning. Automate purchase order generation, production scheduling, and supplier coordination.",
    metrics: [
      { value: "60%", label: "Faster Order Cycle Time" },
      { value: "85%", label: "On-Time Supplier Delivery" },
      { value: "30%", label: "Reduction in Rush Orders" },
      { value: "95%", label: "Planning Automation Rate" },
    ],
    capabilities: [
      {
        title: "Automated PO generation from forecasts",
        icon: "FileText",
        color: "blue",
      },
      {
        title: "Production scheduling and capacity planning",
        icon: "Factory",
        color: "orange",
      },
      {
        title: "Supplier lead time and MOQ management",
        icon: "Users",
        color: "green",
      },
      {
        title: "Order consolidation and optimization",
        icon: "Package",
        color: "purple",
      },
    ],
  },
  // Execution Systems
  {
    id: "lite-tms",
    category: "Execution Systems",
    name: "Lite TMS for SMBs",
    tagline: "Enterprise TMS power for small businesses",
    icon: "Truck",
    description:
      "Affordable, easy-to-deploy Transportation Management System designed for small and mid-sized businesses. Get carrier management, route optimization, and freight tracking without enterprise complexity.",
    metrics: [
      { value: "40%", label: "Reduction in Freight Costs" },
      { value: "95%", label: "On-Time Delivery Rate" },
      { value: "2 weeks", label: "Time to Go-Live" },
      { value: "50%", label: "Less Manual Work" },
    ],
    capabilities: [
      {
        title: "Multi-carrier rate shopping",
        icon: "DollarSign",
        color: "green",
      },
      {
        title: "Route optimization and planning",
        icon: "Route",
        color: "blue",
      },
      { title: "Real-time shipment tracking", icon: "MapPin", color: "purple" },
      {
        title: "Automated carrier communication",
        icon: "MessageSquare",
        color: "orange",
      },
    ],
  },
  {
    id: "warehouse-management",
    category: "Execution Systems",
    name: "Warehouse Management System",
    tagline: "AI-powered warehouse operations",
    icon: "Building2",
    description:
      "Modern WMS that optimizes receiving, putaway, picking, packing, and shipping operations. Real-time inventory visibility, task optimization, and labor management in one platform.",
    metrics: [
      { value: "45%", label: "Improvement in Pick Accuracy" },
      { value: "35%", label: "Labor Productivity Gain" },
      { value: "99.9%", label: "Inventory Accuracy" },
      { value: "60%", label: "Faster Order Fulfillment" },
    ],
    capabilities: [
      {
        title: "Intelligent wave planning and picking",
        icon: "Waves",
        color: "blue",
      },
      {
        title: "Slotting optimization and putaway",
        icon: "Grid3x3",
        color: "green",
      },
      {
        title: "Labor management and task assignment",
        icon: "Users",
        color: "purple",
      },
      {
        title: "Real-time inventory tracking (RFID/barcode)",
        icon: "Scan",
        color: "orange",
      },
    ],
  },
  {
    id: "dispatch-management",
    category: "Execution Systems",
    name: "Dispatch Management System",
    tagline: "Smart last-mile delivery orchestration",
    icon: "MapPin",
    description:
      "Optimize last-mile delivery with AI-powered route planning, driver assignment, real-time tracking, and customer communication. Perfect for field service and delivery operations.",
    metrics: [
      { value: "30%", label: "More Deliveries Per Route" },
      { value: "25%", label: "Reduction in Fuel Costs" },
      { value: "98%", label: "Customer Satisfaction" },
      { value: "15min", label: "Average Time Saved Per Stop" },
    ],
    capabilities: [
      { title: "AI-powered route optimization", icon: "Route", color: "blue" },
      { title: "Dynamic driver assignment", icon: "UserCheck", color: "green" },
      {
        title: "Live GPS tracking and ETAs",
        icon: "Navigation",
        color: "purple",
      },
      {
        title: "Automated customer notifications",
        icon: "Bell",
        color: "orange",
      },
    ],
  },
  {
    id: "freight-forwarding",
    category: "Execution Systems",
    name: "Freight Forwarding Platform",
    tagline: "End-to-end freight visibility and control",
    icon: "Ship",
    description:
      "Comprehensive platform for freight forwarders managing international shipments. Handle bookings, documentation, customs clearance, and tracking across all transportation modes.",
    metrics: [
      { value: "50%", label: "Faster Quote Generation" },
      { value: "90%", label: "Documentation Accuracy" },
      { value: "100%", label: "Shipment Visibility" },
      { value: "40%", label: "Process Automation" },
    ],
    capabilities: [
      { title: "Multi-modal shipment booking", icon: "Globe", color: "blue" },
      {
        title: "Automated customs and compliance docs",
        icon: "FileCheck",
        color: "green",
      },
      {
        title: "Real-time container and cargo tracking",
        icon: "Container",
        color: "purple",
      },
      {
        title: "Carrier and vendor management",
        icon: "Briefcase",
        color: "orange",
      },
    ],
  },
  // Integration & Automation
  {
    id: "edifai",
    category: "Integration & Automation",
    name: "EDIFAI",
    tagline: "Intelligent EDI and B2B integration",
    icon: "Link",
    description:
      "Modern EDI platform powered by AI for automated document translation, validation, and routing. Connect with trading partners faster with pre-built mappings and smart error resolution.",
    metrics: [
      { value: "70%", label: "Faster Partner Onboarding" },
      { value: "95%", label: "Transaction Success Rate" },
      { value: "80%", label: "Less Manual Intervention" },
      { value: "100+", label: "EDI Document Types" },
    ],
    capabilities: [
      { title: "AI-powered document mapping", icon: "Brain", color: "purple" },
      {
        title: "Real-time validation and error handling",
        icon: "AlertCircle",
        color: "red",
      },
      { title: "Pre-built partner connections", icon: "Puzzle", color: "blue" },
      { title: "Cloud-based EDI translation", icon: "Cloud", color: "cyan" },
    ],
  },
  {
    id: "ai-rule-engine",
    category: "Integration & Automation",
    name: "AI Rule Engine",
    tagline: "Intelligent business rules automation",
    icon: "MessageSquare",
    description:
      "Configure and manage complex business rules without coding. AI-powered decision engine that learns from patterns and suggests optimizations for routing, pricing, and workflow automation.",
    metrics: [
      { value: "10x", label: "Faster Rule Deployment" },
      { value: "99%", label: "Rule Execution Accuracy" },
      { value: "500+", label: "Rules Per Second" },
      { value: "60%", label: "Reduction in Exceptions" },
    ],
    capabilities: [
      { title: "No-code rule configuration", icon: "Sliders", color: "blue" },
      {
        title: "ML-powered rule optimization",
        icon: "Sparkles",
        color: "purple",
      },
      { title: "Real-time decision execution", icon: "Zap", color: "yellow" },
      {
        title: "Version control and testing",
        icon: "GitBranch",
        color: "green",
      },
    ],
  },
  {
    id: "document-compliance",
    category: "Integration & Automation",
    name: "Document & Compliance Automation",
    tagline: "Automated document processing and compliance",
    icon: "FileText",
    description:
      "Extract, validate, and process shipping documents, customs forms, invoices, and compliance paperwork using AI. Reduce manual data entry and ensure regulatory compliance across borders.",
    metrics: [
      { value: "90%", label: "Document Processing Accuracy" },
      { value: "5min", label: "Average Processing Time" },
      { value: "100%", label: "Compliance Audit Trail" },
      { value: "75%", label: "Less Manual Entry" },
    ],
    capabilities: [
      {
        title: "OCR and intelligent document extraction",
        icon: "ScanLine",
        color: "blue",
      },
      {
        title: "Automated compliance validation",
        icon: "ShieldCheck",
        color: "green",
      },
      {
        title: "Multi-format document support",
        icon: "Files",
        color: "purple",
      },
      {
        title: "Audit trail and version history",
        icon: "History",
        color: "orange",
      },
    ],
  },
];

export default products;
